import React from 'react';
import pick from 'lodash/pick';
import appConstants from '../../../appConstants';
import { getCustomerAndShipperComment } from '../utils';

const CUSTOMER_GRID_CONFIG_FLAGS = [
  'enableFMECustomer',
  'enableNoCargoVanCustomer',
  'enableFixedEquipmentSize',
  'enableTrailerBackFillCustomer',
  'enableRequiresAppointmentInCustomerManagement',
  'enableIgnoreAppointmentInCustomerManagement',
  'enableCommentsForCustomer',
  'enableNonLGCustomer',
  'enableUnattendedDeliveryInCustomerManagement',
  'enableProofOfDeliveryInCustomerManagement',
  'drPreferenceInCustomerManagement',
  'enableAdvanceDeliveryNotificationInIBPlanning',
];

const SHIPPER_GRID_CONFIG_FLAGS = [
  'enableCommentsForCustomer',
  'enableEquipmentSizeForShipper',
  'enableFixedEquipmentSize',
  'enableUnattendedDeliveryInCustomerManagement',
  'enableProofOfDeliveryInCustomerManagement',
  'enableDRPreferenceInShipperManagement',
];

export const getCustomerGridFeatureFlags = ({ configJSON = {}, featureFlags }) => ({
  ...pick(configJSON, CUSTOMER_GRID_CONFIG_FLAGS),
  enableTailLoadInCustomerManagement: featureFlags?.isFeatureEnabled(
    appConstants.features.EnableTailLoadInCustomerManagement
  ),
});

export const getShipperGridFeatureFlags = ({ configJSON = {} }) =>
  pick(configJSON, SHIPPER_GRID_CONFIG_FLAGS);


/**
 * Processes customer data to extract unique equipment sizes and routes
 * @param {Array} customerData - Array of customer objects containing delivery details
 * @returns {Object} Object containing arrays of unique equipment sizes and routes
 */
export const getUniqueCustomerFilterData = (customerData) => {
  const maxEquipSizes = new Set();
  const minEquipSizes = new Set();
  const fixedEquipSizes = new Set();
  const overrideRoutes = new Set();

  customerData.forEach((customer) => {
    const { maxEquipmentSize, minEquipmentSize, fixedEquipmentSize, route } =
      customer.deliveryDetails;
    maxEquipmentSize && maxEquipSizes.add(maxEquipmentSize);
    minEquipmentSize && minEquipSizes.add(minEquipmentSize);
    fixedEquipmentSize && fixedEquipSizes.add(fixedEquipmentSize);
    route && overrideRoutes.add(route);
  });

  return {
    maxEquipSizes: Array.from(maxEquipSizes),
    minEquipSizes: Array.from(minEquipSizes),
    fixedEquipSizes: Array.from(fixedEquipSizes),
    overrideRoutes: Array.from(overrideRoutes)
  };
};

/**
 * Processes customer data to extract unique equipment sizes and routes
 * @param {Array} customerData - Array of customer objects containing pickup details
 * @returns {Object} Object containing arrays of unique equipment sizes and routes
 */
export const getUniqueShipperFilterData = (shipperData) => {
  const maxEquipSizes = new Set();
  const minEquipSizes = new Set();
  const fixedEquipSizes = new Set();

  shipperData.forEach((shipper) => {
    const { maxEquipmentSize, minEquipmentSize, fixedEquipmentSize } = shipper.pickupDetails ?? {};
    maxEquipmentSize && maxEquipSizes.add(maxEquipmentSize);
    minEquipmentSize && minEquipSizes.add(minEquipmentSize);
    fixedEquipmentSize && fixedEquipSizes.add(fixedEquipmentSize);
  });

  return {
    maxEquipSizes: Array.from(maxEquipSizes),
    minEquipSizes: Array.from(minEquipSizes),
    fixedEquipSizes: Array.from(fixedEquipSizes)
  };
};

const getSpecialNeedsData = ({ data, isDelivery, featureFlags }) => {
  const {
    isLiftGateRequired,
    isApt,
    isTrailerBackFill,
    isUnattendedDelivery,
    isTrailerSwap,
    specialInstruction,
    isADNRequired,
    isTailLoad,
    isPhysicalDrRequired
  } = data;

  const { customerComments, shipperComments } = featureFlags?.enableCommentsForCustomer
    ? getCustomerAndShipperComment({ ...data, isDelivery })
    : { customerComments: null, shipperComments: null };

  const liftgate = isLiftGateRequired ? appConstants.SpecialNeedsStrings.L : '';
  const trailerBackFill = isTrailerBackFill ? appConstants.TRAILER_BACKFILL.YES : '';
  const apt = isApt ? appConstants.SpecialNeedsStrings.APT : '';

  return {
    apt,
    isADNRequired,
    liftgate,
    specialInstruction,
    customerComments,
    shipperComments,
    isDelivery,
    isTrailerSwap,
    isUnattendedDelivery,
    isTrailerBackFill: trailerBackFill,
    isTailLoad,
    isPhysicalDrRequired
  };
};

const getEquipmentSizeTooltipContent = ({
  maxEquipmentSize,
  minEquipmentSize,
  fixedEquipmentSize
}) => {
  const sizeMap = {
    'Fixed Size': fixedEquipmentSize,
    'Max Size': maxEquipmentSize,
    'Min Size': minEquipmentSize
  };

  return (
    <div>
      {Object.entries(sizeMap)
        .filter(([_, val]) => val)
        .map(([label, val], index) => (
          <div key={index}>{`${label}: ${val}`}</div>
        ))}
    </div>
  );
};

export const formatBooleanYesNo = (params) =>
  params.value ? appConstants.BOOLEAN_FLAG.YES : '';

export const getAliasCodes = (params) => (params.data?.aliasCodes ?? []).join(', ');

export { getSpecialNeedsData, getEquipmentSizeTooltipContent };
